import type { FC } from "react";
import { useEffect, useState } from "react";

import { StickAttributes } from "@ui/components/StickAttributes";
import { useTranslations } from "@ui/providers/useTranslations";
import { getOptimizedMagentoImageUrl } from "@ui/utils/getOptimizedMagentoImageUrl";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { PackageCrossIcon } from "@jti/brand-icons/package-cross";
import { StoreDefaultIcon } from "@jti/brand-icons/store-default";
import { Loader } from "@jti/core-components/components/Loader";
import { QuantityInput } from "@jti/core-components/components/QuantityInput";
import type { MagentoFlavorAttribute, MagentoStockStatus } from "@jti/magento-types";

import styles from "./MultipleFlavorSelectorSticksBlock.module.scss";

// Matches the hardcoded 100×100px .image dimensions in MultipleFlavorSelectorSticksBlock.module.scss.
const FLAVOR_IMAGE_SIZE_PX = 100;
const FLAVOR_IMAGE_DENSITIES = [1, 2, 3] as const;

const getOptimizedFlavorImageUrl = (imageUrl: string, density: number) =>
  getOptimizedMagentoImageUrl({
    url: imageUrl,
    width: FLAVOR_IMAGE_SIZE_PX * density,
    height: FLAVOR_IMAGE_SIZE_PX * density,
  });

export type MultipleFlavorSelectorSticksBlockProps = {
  label: string;
  description: string;
  id: string | number;
  imageUrl: string;
  stockStatus: MagentoStockStatus;
  isLimitReached: boolean;
  initialQuantity?: number;
  onChange: (id: string | number, quantity: number) => void;
  isStockStatusLoading?: boolean;
  attributes: MagentoFlavorAttribute[];
};

export const MultipleFlavorSelectorSticksBlock: FC<MultipleFlavorSelectorSticksBlockProps> = ({
  label,
  description,
  id,
  imageUrl,
  stockStatus,
  isLimitReached,
  initialQuantity = 0,
  onChange,
  isStockStatusLoading,
  attributes,
}) => {
  const [quantity, setQuantity] = useState(initialQuantity);
  const { showFlavourAttributesInFlavourSelector = false, showFlavourDescriptionInFlavourSelector = false } =
    useConfig();
  const { __ } = useTranslations();
  const isInStock = stockStatus === "IN_STOCK";
  const isAvailableInStores = stockStatus === "AVAILABLE_IN_STORES";
  const optimizedImageUrls = imageUrl
    ? FLAVOR_IMAGE_DENSITIES.map((density) => getOptimizedFlavorImageUrl(imageUrl, density))
    : [];
  const optimizedImageSrcSet = imageUrl
    ? optimizedImageUrls
        .map((optimizedImageUrl, index) => `${optimizedImageUrl} ${FLAVOR_IMAGE_DENSITIES[index]}x`)
        .join(", ")
    : undefined;

  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  const onQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity);
    onChange(id, newQuantity);
  };

  return (
    <section
      className={classNames(styles.block, {
        [styles.inStock]: isInStock,
        [styles.outOfStock]: !isInStock,
      })}
      data-testid="multiple-flavor-selector-sticks-block"
    >
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <img
            className={styles.image}
            data-testid="multiple-flavor-selector-image"
            src={optimizedImageUrls[0]}
            srcSet={optimizedImageSrcSet}
            alt=""
            loading="lazy"
            decoding="async"
          />
          <span className={styles.label}>{label}</span>

          {showFlavourDescriptionInFlavourSelector && description && (
            <span className={styles.subheader} data-testid="flavourDescription">
              {description}
            </span>
          )}
        </div>
        {showFlavourAttributesInFlavourSelector && attributes && <StickAttributes attributes={attributes} />}
      </header>
      <div className={styles.content}>
        <div className={styles.actions}>
          {isStockStatusLoading && <Loader data-testid="multipleFlavorSelectorSticksBlockLoader" />}

          {!isStockStatusLoading && isInStock && (
            <QuantityInput
              onChange={onQuantityChange}
              value={quantity}
              disabledAdd={isLimitReached}
              min={0}
              aria-label={__("productdetails.sticks.quantity.input", [label])}
              increaseButtonAriaLabel={__("productdetails.sticks.quantity.increase", [label])}
              decreaseButtonAriaLabel={__("productdetails.sticks.quantity.decrease", [label])}
            />
          )}

          {!isStockStatusLoading && isAvailableInStores && (
            <div className={styles.stockInfo}>
              <StoreDefaultIcon />
              <div className={styles.stockInfoContent}>{__("product.label.availableinstores")}</div>
            </div>
          )}

          {!isStockStatusLoading && !isInStock && !isAvailableInStores && (
            <div className={styles.stockInfo}>
              <div aria-hidden="true" className={styles.warningDot}></div>
              <PackageCrossIcon aria-hidden="true" />
              <div className={styles.stockInfoContent}>{__("productdetails.sticks.outOfStock")}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
